import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  LayoutDashboard,
  FileText,
  Image,
  Settings,
  Mail,
  LogOut,
  Menu,
  X,
  Plus,
  Trash2,
  Edit2,
  Save,
  Eye,
  GripVertical,
  Sliders,
  Navigation,
  Lock,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Layers,
  Globe,
  Bed,
  Waves
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');

  // Data states
  const [content, setContent] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [messages, setMessages] = useState([]);
  const [settings, setSettings] = useState({});
  const [menuItems, setMenuItems] = useState([]);
  const [sliderItems, setSliderItems] = useState([]);
  const [pages, setPages] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [pools, setPools] = useState([]);
  const [loading, setLoading] = useState(true);

  // Room & Pool editing states
  const [editingRoom, setEditingRoom] = useState(null);
  const [editingPool, setEditingPool] = useState(null);

  // New room and pool forms
  const [newRoom, setNewRoom] = useState({
    title: { tr: '', en: '', de: '', ru: '' },
    subtitle: { tr: '', en: '', de: '', ru: '' },
    size: { tr: '', en: '', de: '', ru: '' },
    description: { tr: '', en: '', de: '', ru: '' },
    image: '',
    images: [],
    views: { tr: [], en: [], de: [], ru: [] },
    features: { tr: [], en: [], de: [], ru: [] },
    highlight: false,
    order: 0
  });

  const [newPool, setNewPool] = useState({
    name: { tr: '', en: '', de: '', ru: '' },
    size: '',
    depth: '',
    type: 'outdoor',
    order: 0
  });

  // Form states
  const [editingContent, setEditingContent] = useState(null);
  const [editingSettings, setEditingSettings] = useState(false);
  const [editingMenu, setEditingMenu] = useState(null);
  const [editingSlider, setEditingSlider] = useState(null);
  const [editingPage, setEditingPage] = useState(null);

  // New item forms
  const [newGalleryImage, setNewGalleryImage] = useState({ url: '', category: 'rooms', alt_text: { tr: '', en: '', de: '', ru: '' }, order: 0 });
  const [newMenuItem, setNewMenuItem] = useState({ path: '', labels: { tr: '', en: '', de: '', ru: '' }, order: 0, is_active: true, is_external: false });
  const [newSlider, setNewSlider] = useState({ url: '', titles: { tr: '', en: '', de: '', ru: '' }, subtitles: { tr: '', en: '', de: '', ru: '' }, order: 0, is_active: true });
  const [newContent, setNewContent] = useState({ key: '', section: '', translations: { tr: '', en: '', de: '', ru: '' } });

  // Password change
  const [passwordData, setPasswordData] = useState({ old_password: '', new_password: '', confirm_password: '' });
  const [showPasswordForm, setShowPasswordForm] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [contentRes, galleryRes, messagesRes, settingsRes, menuRes, sliderRes, pagesRes, roomsRes, poolsRes] = await Promise.all([
        axios.get(`${API}/content`, { headers }),
        axios.get(`${API}/gallery`, { headers }),
        axios.get(`${API}/contact/messages`, { headers }),
        axios.get(`${API}/settings`, { headers }),
        axios.get(`${API}/menu`, { headers }),
        axios.get(`${API}/slider`, { headers }),
        axios.get(`${API}/pages`, { headers }),
        axios.get(`${API}/rooms`, { headers }),
        axios.get(`${API}/pools`, { headers }),
      ]);
      setContent(contentRes.data);
      setGallery(galleryRes.data);
      setMessages(messagesRes.data);
      setSettings(settingsRes.data);
      setMenuItems(menuRes.data);
      setSliderItems(sliderRes.data);
      setPages(pagesRes.data);
      setRooms(roomsRes.data);
      setPools(poolsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Veri yüklenirken hata oluştu');
    }
    setLoading(false);
  };

  // Rooms CRUD handlers
  const handleAddRoom = async () => {
    if (!newRoom.title.tr || !newRoom.image) {
      toast.error('Türkçe Başlık ve Görsel URL zorunludur');
      return;
    }
    try {
      await axios.post(`${API}/rooms`, newRoom, { headers });
      toast.success('Oda eklendi');
      setNewRoom({
        title: { tr: '', en: '', de: '', ru: '' },
        subtitle: { tr: '', en: '', de: '', ru: '' },
        size: { tr: '', en: '', de: '', ru: '' },
        description: { tr: '', en: '', de: '', ru: '' },
        image: '',
        images: [],
        views: { tr: [], en: [], de: [], ru: [] },
        features: { tr: [], en: [], de: [], ru: [] },
        highlight: false,
        order: 0
      });
      fetchData();
    } catch (error) {
      toast.error('Oda eklenirken hata oluştu');
    }
  };

  const handleUpdateRoom = async (room) => {
    try {
      await axios.put(`${API}/rooms/${room.id}`, room, { headers });
      toast.success('Oda güncellendi');
      setEditingRoom(null);
      fetchData();
    } catch (error) {
      toast.error('Oda güncellenirken hata oluştu');
    }
  };

  const handleDeleteRoom = async (roomId) => {
    if (!window.confirm('Bu odayı silmek istediğinizden emin misiniz?')) return;
    try {
      await axios.delete(`${API}/rooms/${roomId}`, { headers });
      toast.success('Oda silindi');
      fetchData();
    } catch (error) {
      toast.error('Oda silinirken hata oluştu');
    }
  };

  // Pools CRUD handlers
  const handleAddPool = async () => {
    if (!newPool.name.tr) {
      toast.error('Türkçe Havuz Adı zorunludur');
      return;
    }
    try {
      await axios.post(`${API}/pools`, newPool, { headers });
      toast.success('Havuz eklendi');
      setNewPool({
        name: { tr: '', en: '', de: '', ru: '' },
        size: '',
        depth: '',
        type: 'outdoor',
        order: 0
      });
      fetchData();
    } catch (error) {
      toast.error('Havuz eklenirken hata oluştu');
    }
  };

  const handleUpdatePool = async (pool) => {
    try {
      await axios.put(`${API}/pools/${pool.id}`, pool, { headers });
      toast.success('Havuz güncellendi');
      setEditingPool(null);
      fetchData();
    } catch (error) {
      toast.error('Havuz güncellenirken hata oluştu');
    }
  };

  const handleDeletePool = async (poolId) => {
    if (!window.confirm('Bu havuzu silmek istediğinizden emin misiniz?')) return;
    try {
      await axios.delete(`${API}/pools/${poolId}`, { headers });
      toast.success('Havuz silindi');
      fetchData();
    } catch (error) {
      toast.error('Havuz silinirken hata oluştu');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  // Content handlers
  const handleContentUpdate = async (contentItem) => {
    try {
      await axios.put(`${API}/content/${contentItem.id}`, {
        key: contentItem.key,
        section: contentItem.section,
        translations: contentItem.translations
      }, { headers });
      toast.success('İçerik güncellendi');
      fetchData();
      setEditingContent(null);
    } catch (error) {
      toast.error('Güncelleme başarısız');
    }
  };

  const handleAddContent = async () => {
    if (!newContent.key || !newContent.section) {
      toast.error('Anahtar ve bölüm zorunludur');
      return;
    }
    try {
      await axios.post(`${API}/content`, newContent, { headers });
      toast.success('İçerik eklendi');
      setNewContent({ key: '', section: '', translations: { tr: '', en: '', de: '', ru: '' } });
      fetchData();
    } catch (error) {
      toast.error('Ekleme başarısız');
    }
  };

  const handleDeleteContent = async (contentId) => {
    if (!window.confirm('Bu içeriği silmek istediğinizden emin misiniz?')) return;
    try {
      await axios.delete(`${API}/content/${contentId}`, { headers });
      toast.success('İçerik silindi');
      fetchData();
    } catch (error) {
      toast.error('Silme başarısız');
    }
  };

  // Gallery handlers
  const handleDeleteGalleryImage = async (imageId) => {
    if (!window.confirm('Bu görseli silmek istediğinizden emin misiniz?')) return;
    try {
      await axios.delete(`${API}/gallery/${imageId}`, { headers });
      toast.success('Görsel silindi');
      fetchData();
    } catch (error) {
      toast.error('Silme başarısız');
    }
  };

  const handleAddGalleryImage = async () => {
    if (!newGalleryImage.url) {
      toast.error('Görsel URL zorunludur');
      return;
    }
    try {
      await axios.post(`${API}/gallery`, newGalleryImage, { headers });
      toast.success('Görsel eklendi');
      setNewGalleryImage({ url: '', category: 'rooms', alt_text: { tr: '', en: '', de: '', ru: '' }, order: 0 });
      fetchData();
    } catch (error) {
      toast.error('Ekleme başarısız');
    }
  };

  // Menu handlers
  const handleAddMenuItem = async () => {
    if (!newMenuItem.path || !newMenuItem.labels.tr) {
      toast.error('Path ve Türkçe etiket zorunludur');
      return;
    }
    try {
      await axios.post(`${API}/menu`, newMenuItem, { headers });
      toast.success('Menü öğesi eklendi');
      setNewMenuItem({ path: '', labels: { tr: '', en: '', de: '', ru: '' }, order: 0, is_active: true, is_external: false });
      fetchData();
    } catch (error) {
      toast.error('Ekleme başarısız');
    }
  };

  const handleUpdateMenuItem = async (item) => {
    try {
      await axios.put(`${API}/menu/${item.id}`, {
        path: item.path,
        labels: item.labels,
        order: item.order,
        is_active: item.is_active,
        is_external: item.is_external
      }, { headers });
      toast.success('Menü güncellendi');
      setEditingMenu(null);
      fetchData();
    } catch (error) {
      toast.error('Güncelleme başarısız');
    }
  };

  const handleDeleteMenuItem = async (itemId) => {
    if (!window.confirm('Bu menü öğesini silmek istediğinizden emin misiniz?')) return;
    try {
      await axios.delete(`${API}/menu/${itemId}`, { headers });
      toast.success('Menü öğesi silindi');
      fetchData();
    } catch (error) {
      toast.error('Silme başarısız');
    }
  };

  // Slider handlers
  const handleAddSlider = async () => {
    if (!newSlider.url) {
      toast.error('Görsel URL zorunludur');
      return;
    }
    try {
      await axios.post(`${API}/slider`, newSlider, { headers });
      toast.success('Slider eklendi');
      setNewSlider({ url: '', titles: { tr: '', en: '', de: '', ru: '' }, subtitles: { tr: '', en: '', de: '', ru: '' }, order: 0, is_active: true });
      fetchData();
    } catch (error) {
      toast.error('Ekleme başarısız');
    }
  };

  const handleUpdateSlider = async (slide) => {
    try {
      await axios.put(`${API}/slider/${slide.id}`, {
        url: slide.url,
        titles: slide.titles,
        subtitles: slide.subtitles,
        order: slide.order,
        is_active: slide.is_active
      }, { headers });
      toast.success('Slider güncellendi');
      setEditingSlider(null);
      fetchData();
    } catch (error) {
      toast.error('Güncelleme başarısız');
    }
  };

  const handleDeleteSlider = async (slideId) => {
    if (!window.confirm('Bu slider\'ı silmek istediğinizden emin misiniz?')) return;
    try {
      await axios.delete(`${API}/slider/${slideId}`, { headers });
      toast.success('Slider silindi');
      fetchData();
    } catch (error) {
      toast.error('Silme başarısız');
    }
  };

  // Settings handler
  const handleSettingsUpdate = async () => {
    try {
      await axios.put(`${API}/settings`, settings, { headers });
      toast.success('Ayarlar güncellendi');
      setEditingSettings(false);
      fetchData();
    } catch (error) {
      toast.error('Güncelleme başarısız');
    }
  };

  // Password change handler
  const handlePasswordChange = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Yeni şifreler eşleşmiyor');
      return;
    }
    if (passwordData.new_password.length < 6) {
      toast.error('Şifre en az 6 karakter olmalı');
      return;
    }
    try {
      await axios.put(`${API}/auth/password`, {
        old_password: passwordData.old_password,
        new_password: passwordData.new_password
      }, { headers });
      toast.success('Şifre değiştirildi');
      setPasswordData({ old_password: '', new_password: '', confirm_password: '' });
      setShowPasswordForm(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Şifre değiştirilemedi');
    }
  };

  // Message handlers
  const handleMarkMessageRead = async (messageId) => {
    try {
      await axios.put(`${API}/contact/messages/${messageId}/read`, {}, { headers });
      fetchData();
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Bu mesajı silmek istediğinizden emin misiniz?')) return;
    try {
      await axios.delete(`${API}/contact/messages/${messageId}`, { headers });
      toast.success('Mesaj silindi');
      fetchData();
    } catch (error) {
      toast.error('Silme başarısız');
    }
  };

  // Page handlers
  const handlePageUpdate = async (pageKey, pageData) => {
    try {
      await axios.put(`${API}/pages/${pageKey}`, pageData, { headers });
      toast.success('Sayfa güncellendi');
      setEditingPage(null);
      fetchData();
    } catch (error) {
      toast.error('Güncelleme başarısız');
    }
  };

  const handleAddSection = (pageKey) => {
    const currentPage = pages.find(p => p.page_key === pageKey);
    if (!currentPage) return;

    const newSection = {
      id: Date.now().toString(),
      type: 'text',
      title: { tr: '', en: '', de: '', ru: '' },
      content: { tr: '', en: '', de: '', ru: '' },
      image: '',
      order: currentPage.sections?.length || 0
    };

    setEditingPage({
      ...currentPage,
      sections: [...(currentPage.sections || []), newSection]
    });
  };

  const handleRemoveSection = (sectionId) => {
    if (!editingPage) return;
    setEditingPage({
      ...editingPage,
      sections: editingPage.sections.filter(s => s.id !== sectionId)
    });
  };

  const handleUpdateSection = (sectionId, field, value) => {
    if (!editingPage) return;
    setEditingPage({
      ...editingPage,
      sections: editingPage.sections.map(s =>
        s.id === sectionId ? { ...s, [field]: value } : s
      )
    });
  };

  const menuNavItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'slider', label: 'Slider Görselleri', icon: Sliders },
    { id: 'menu', label: 'Menü Yönetimi', icon: Navigation },
    { id: 'pages', label: 'Sayfa Yönetimi', icon: Layers },
    { id: 'rooms', label: 'Oda Yönetimi', icon: Bed },
    { id: 'pools', label: 'Havuz Yönetimi', icon: Waves },
    { id: 'content', label: 'İçerik Yönetimi', icon: FileText },
    { id: 'gallery', label: 'Galeri', icon: Image },
    { id: 'messages', label: 'Mesajlar', icon: Mail, badge: messages.filter(m => !m.is_read).length },
    { id: 'settings', label: 'Site Ayarları', icon: Settings },
    { id: 'security', label: 'Güvenlik', icon: Lock },
  ];

  const galleryCategories = [
    { value: 'rooms', label: 'Odalar' },
    { value: 'restaurants', label: 'Restoranlar' },
    { value: 'pool', label: 'Havuz' },
    { value: 'beach', label: 'Plaj' },
    { value: 'spa', label: 'Spa' },
    { value: 'general', label: 'Genel' },
  ];

  return (
    <div className="min-h-screen bg-gray-100" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-0 h-full bg-gradient-to-b from-holiday-purple to-holiday-purple-dark text-white z-40 transition-all duration-300 ${sidebarOpen ? 'w-64' : 'w-16'
          }`}
      >
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center justify-between">
            {sidebarOpen && (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <span className="text-xl font-heading font-bold">H</span>
                </div>
                <div>
                  <h2 className="font-heading font-bold text-sm">Admin Panel</h2>
                  <p className="text-[10px] text-white/60">Holiday Inn Bodrum</p>
                </div>
              </div>
            )}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 hover:bg-white/10 rounded"
              data-testid="sidebar-toggle"
            >
              {sidebarOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>

        <nav className="p-2 space-y-1 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 140px)' }}>
          {menuNavItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${activeTab === item.id ? 'bg-white/20' : 'hover:bg-white/10'
                }`}
              data-testid={`nav-${item.id}`}
            >
              <item.icon size={18} />
              {sidebarOpen && (
                <>
                  <span className="flex-1 text-left text-sm">{item.label}</span>
                  {item.badge > 0 && (
                    <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                      {item.badge}
                    </span>
                  )}
                </>
              )}
            </button>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-2 border-t border-white/10">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/10"
            data-testid="logout-btn"
          >
            <LogOut size={18} />
            {sidebarOpen && <span className="text-sm">Çıkış Yap</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`transition-all duration-300 ${sidebarOpen ? 'ml-64' : 'ml-16'}`}>
        {/* Header */}
        <header className="bg-white shadow-sm p-4 sticky top-0 z-30">
          <div className="flex items-center justify-between">
            <h1 className="text-lg font-heading font-bold text-gray-900">
              {menuNavItems.find(m => m.id === activeTab)?.label}
            </h1>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-500">Hoş geldiniz, {user?.username}</span>
              <Link
                to="/"
                target="_blank"
                className="text-sm text-holiday-purple hover:underline flex items-center gap-1"
              >
                Siteyi Görüntüle <ExternalLink size={14} />
              </Link>
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="p-4 md:p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="loading-spinner" />
            </div>
          ) : (
            <>
              {/* Dashboard Tab */}
              {activeTab === 'dashboard' && (
                <div className="space-y-6" data-testid="dashboard-stats">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-white p-6 rounded-xl shadow-sm">
                      <h3 className="text-gray-500 text-sm">Slider</h3>
                      <p className="text-3xl font-bold text-holiday-purple">{sliderItems.length}</p>
                    </div>
                    <div className="bg-white p-6 rounded-xl shadow-sm">
                      <h3 className="text-gray-500 text-sm">Menü Öğeleri</h3>
                      <p className="text-3xl font-bold text-holiday-purple">{menuItems.length}</p>
                    </div>
                    <div className="bg-white p-6 rounded-xl shadow-sm">
                      <h3 className="text-gray-500 text-sm">Galeri</h3>
                      <p className="text-3xl font-bold text-holiday-purple">{gallery.length}</p>
                    </div>
                    <div className="bg-white p-6 rounded-xl shadow-sm">
                      <h3 className="text-gray-500 text-sm">Okunmamış</h3>
                      <p className="text-3xl font-bold text-red-500">{messages.filter(m => !m.is_read).length}</p>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <h3 className="font-semibold mb-4">Hızlı Erişim</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <button onClick={() => setActiveTab('slider')} className="p-4 border rounded-lg hover:bg-gray-50 text-center">
                        <Sliders className="w-8 h-8 mx-auto mb-2 text-holiday-purple" />
                        <span className="text-sm">Slider Düzenle</span>
                      </button>
                      <button onClick={() => setActiveTab('menu')} className="p-4 border rounded-lg hover:bg-gray-50 text-center">
                        <Navigation className="w-8 h-8 mx-auto mb-2 text-holiday-purple" />
                        <span className="text-sm">Menü Düzenle</span>
                      </button>
                      <button onClick={() => setActiveTab('gallery')} className="p-4 border rounded-lg hover:bg-gray-50 text-center">
                        <Image className="w-8 h-8 mx-auto mb-2 text-holiday-purple" />
                        <span className="text-sm">Galeri Ekle</span>
                      </button>
                      <button onClick={() => setActiveTab('settings')} className="p-4 border rounded-lg hover:bg-gray-50 text-center">
                        <Settings className="w-8 h-8 mx-auto mb-2 text-holiday-purple" />
                        <span className="text-sm">Ayarlar</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Slider Tab */}
              {activeTab === 'slider' && (
                <div className="space-y-6" data-testid="slider-management">
                  {/* Add new slider */}
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <h3 className="font-semibold mb-4">Yeni Slider Ekle</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Görsel URL</label>
                        <Input
                          placeholder="https://example.com/image.jpg"
                          value={newSlider.url}
                          onChange={(e) => setNewSlider({ ...newSlider, url: e.target.value })}
                        />
                      </div>
                      <Tabs defaultValue="tr">
                        <TabsList>
                          <TabsTrigger value="tr">TR</TabsTrigger>
                          <TabsTrigger value="en">EN</TabsTrigger>
                          <TabsTrigger value="de">DE</TabsTrigger>
                          <TabsTrigger value="ru">RU</TabsTrigger>
                        </TabsList>
                        {['tr', 'en', 'de', 'ru'].map((lang) => (
                          <TabsContent key={lang} value={lang} className="space-y-3">
                            <Input
                              placeholder={`Başlık (${lang.toUpperCase()})`}
                              value={newSlider.titles[lang] || ''}
                              onChange={(e) => setNewSlider({
                                ...newSlider,
                                titles: { ...newSlider.titles, [lang]: e.target.value }
                              })}
                            />
                            <Input
                              placeholder={`Alt Başlık (${lang.toUpperCase()})`}
                              value={newSlider.subtitles[lang] || ''}
                              onChange={(e) => setNewSlider({
                                ...newSlider,
                                subtitles: { ...newSlider.subtitles, [lang]: e.target.value }
                              })}
                            />
                          </TabsContent>
                        ))}
                      </Tabs>
                      <div className="flex gap-4">
                        <Input
                          type="number"
                          placeholder="Sıra"
                          value={newSlider.order}
                          onChange={(e) => setNewSlider({ ...newSlider, order: parseInt(e.target.value) || 0 })}
                          className="w-24"
                        />
                        <Button onClick={handleAddSlider} className="bg-holiday-green hover:bg-holiday-green-dark">
                          <Plus size={16} className="mr-2" /> Slider Ekle
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Existing sliders */}
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <h3 className="font-semibold mb-4">Mevcut Slider'lar ({sliderItems.length})</h3>
                    <div className="space-y-4">
                      {sliderItems.map((slide, index) => (
                        <div key={slide.id} className="border rounded-lg p-4">
                          <div className="flex gap-4">
                            <img src={slide.url} alt="" className="w-40 h-24 object-cover rounded" />
                            <div className="flex-1">
                              {editingSlider?.id === slide.id ? (
                                <div className="space-y-3">
                                  <Input
                                    value={editingSlider.url}
                                    onChange={(e) => setEditingSlider({ ...editingSlider, url: e.target.value })}
                                    placeholder="Görsel URL"
                                  />
                                  <Tabs defaultValue="tr">
                                    <TabsList>
                                      <TabsTrigger value="tr">TR</TabsTrigger>
                                      <TabsTrigger value="en">EN</TabsTrigger>
                                      <TabsTrigger value="de">DE</TabsTrigger>
                                      <TabsTrigger value="ru">RU</TabsTrigger>
                                    </TabsList>
                                    {['tr', 'en', 'de', 'ru'].map((lang) => (
                                      <TabsContent key={lang} value={lang} className="space-y-2">
                                        <Input
                                          placeholder={`Başlık (${lang.toUpperCase()})`}
                                          value={editingSlider.titles?.[lang] || ''}
                                          onChange={(e) => setEditingSlider({
                                            ...editingSlider,
                                            titles: { ...editingSlider.titles, [lang]: e.target.value }
                                          })}
                                        />
                                        <Input
                                          placeholder={`Alt Başlık (${lang.toUpperCase()})`}
                                          value={editingSlider.subtitles?.[lang] || ''}
                                          onChange={(e) => setEditingSlider({
                                            ...editingSlider,
                                            subtitles: { ...editingSlider.subtitles, [lang]: e.target.value }
                                          })}
                                        />
                                      </TabsContent>
                                    ))}
                                  </Tabs>
                                  <div className="flex gap-2">
                                    <Button onClick={() => handleUpdateSlider(editingSlider)} size="sm" className="bg-holiday-purple">
                                      <Save size={14} className="mr-1" /> Kaydet
                                    </Button>
                                    <Button onClick={() => setEditingSlider(null)} size="sm" variant="outline">İptal</Button>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <h4 className="font-medium">{slide.titles?.tr || 'Başlık yok'}</h4>
                                  <p className="text-sm text-gray-500">{slide.subtitles?.tr || 'Alt başlık yok'}</p>
                                  <p className="text-xs text-gray-400 mt-1">Sıra: {slide.order}</p>
                                </>
                              )}
                            </div>
                            {editingSlider?.id !== slide.id && (
                              <div className="flex gap-2">
                                <Button size="sm" variant="outline" onClick={() => setEditingSlider(slide)}>
                                  <Edit2 size={14} />
                                </Button>
                                <Button size="sm" variant="outline" className="text-red-500" onClick={() => handleDeleteSlider(slide.id)}>
                                  <Trash2 size={14} />
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Menu Tab */}
              {activeTab === 'menu' && (
                <div className="space-y-6" data-testid="menu-management">
                  {/* Add new menu item */}
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <h3 className="font-semibold mb-4">Yeni Menü Öğesi Ekle</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Path (URL)</label>
                        <Input
                          placeholder="/sayfa-adi"
                          value={newMenuItem.path}
                          onChange={(e) => setNewMenuItem({ ...newMenuItem, path: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Sıra</label>
                        <Input
                          type="number"
                          value={newMenuItem.order}
                          onChange={(e) => setNewMenuItem({ ...newMenuItem, order: parseInt(e.target.value) || 0 })}
                        />
                      </div>
                    </div>
                    <div className="mt-4">
                      <Tabs defaultValue="tr">
                        <TabsList>
                          <TabsTrigger value="tr">TR</TabsTrigger>
                          <TabsTrigger value="en">EN</TabsTrigger>
                          <TabsTrigger value="de">DE</TabsTrigger>
                          <TabsTrigger value="ru">RU</TabsTrigger>
                        </TabsList>
                        {['tr', 'en', 'de', 'ru'].map((lang) => (
                          <TabsContent key={lang} value={lang}>
                            <Input
                              placeholder={`Menü Etiketi (${lang.toUpperCase()})`}
                              value={newMenuItem.labels[lang] || ''}
                              onChange={(e) => setNewMenuItem({
                                ...newMenuItem,
                                labels: { ...newMenuItem.labels, [lang]: e.target.value }
                              })}
                            />
                          </TabsContent>
                        ))}
                      </Tabs>
                    </div>
                    <div className="flex items-center gap-4 mt-4">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={newMenuItem.is_external}
                          onChange={(e) => setNewMenuItem({ ...newMenuItem, is_external: e.target.checked })}
                          className="rounded"
                        />
                        <span className="text-sm">Harici Link</span>
                      </label>
                      <Button onClick={handleAddMenuItem} className="bg-holiday-green hover:bg-holiday-green-dark">
                        <Plus size={16} className="mr-2" /> Menü Ekle
                      </Button>
                    </div>
                  </div>

                  {/* Existing menu items */}
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <h3 className="font-semibold mb-4">Mevcut Menü Öğeleri ({menuItems.length})</h3>
                    <div className="space-y-2">
                      {menuItems.sort((a, b) => a.order - b.order).map((item) => (
                        <div key={item.id} className="border rounded-lg p-4">
                          {editingMenu?.id === item.id ? (
                            <div className="space-y-3">
                              <div className="grid grid-cols-2 gap-4">
                                <Input
                                  value={editingMenu.path}
                                  onChange={(e) => setEditingMenu({ ...editingMenu, path: e.target.value })}
                                  placeholder="Path"
                                />
                                <Input
                                  type="number"
                                  value={editingMenu.order}
                                  onChange={(e) => setEditingMenu({ ...editingMenu, order: parseInt(e.target.value) || 0 })}
                                  placeholder="Sıra"
                                />
                              </div>
                              <Tabs defaultValue="tr">
                                <TabsList>
                                  <TabsTrigger value="tr">TR</TabsTrigger>
                                  <TabsTrigger value="en">EN</TabsTrigger>
                                  <TabsTrigger value="de">DE</TabsTrigger>
                                  <TabsTrigger value="ru">RU</TabsTrigger>
                                </TabsList>
                                {['tr', 'en', 'de', 'ru'].map((lang) => (
                                  <TabsContent key={lang} value={lang}>
                                    <Input
                                      value={editingMenu.labels?.[lang] || ''}
                                      onChange={(e) => setEditingMenu({
                                        ...editingMenu,
                                        labels: { ...editingMenu.labels, [lang]: e.target.value }
                                      })}
                                      placeholder={`Etiket (${lang.toUpperCase()})`}
                                    />
                                  </TabsContent>
                                ))}
                              </Tabs>
                              <div className="flex gap-2">
                                <Button onClick={() => handleUpdateMenuItem(editingMenu)} size="sm" className="bg-holiday-purple">
                                  <Save size={14} className="mr-1" /> Kaydet
                                </Button>
                                <Button onClick={() => setEditingMenu(null)} size="sm" variant="outline">İptal</Button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                <GripVertical className="w-4 h-4 text-gray-400" />
                                <span className="text-sm text-gray-500 w-8">{item.order}</span>
                                <span className="font-medium">{item.labels?.tr}</span>
                                <span className="text-sm text-gray-500">{item.path}</span>
                                {item.is_external && <ExternalLink className="w-4 h-4 text-gray-400" />}
                              </div>
                              <div className="flex gap-2">
                                <Button size="sm" variant="outline" onClick={() => setEditingMenu(item)}>
                                  <Edit2 size={14} />
                                </Button>
                                <Button size="sm" variant="outline" className="text-red-500" onClick={() => handleDeleteMenuItem(item.id)}>
                                  <Trash2 size={14} />
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Pages Tab - Full Page Content Management */}
              {activeTab === 'pages' && (
                <div className="space-y-6" data-testid="pages-management">
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h2 className="font-semibold text-lg">Sayfa İçerik Yönetimi</h2>
                        <p className="text-sm text-gray-500">Her sayfanın hero görseli, başlık ve içerik bölümlerini düzenleyin</p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      {pages.map((page) => (
                        <div key={page.page_key} className="border rounded-xl overflow-hidden">
                          <div
                            className="flex items-center justify-between p-4 bg-gray-50 cursor-pointer hover:bg-gray-100"
                            onClick={() => editingPage?.page_key === page.page_key ? setEditingPage(null) : setEditingPage({ ...page })}
                          >
                            <div className="flex items-center gap-3">
                              <Globe className="w-5 h-5 text-holiday-purple" />
                              <div>
                                <h3 className="font-medium capitalize">{page.page_key} Sayfası</h3>
                                <p className="text-xs text-gray-500">/{page.page_key}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {page.hero_image && (
                                <img src={page.hero_image} alt="" className="w-16 h-10 object-cover rounded" />
                              )}
                              {editingPage?.page_key === page.page_key ? (
                                <ChevronUp className="w-5 h-5 text-gray-400" />
                              ) : (
                                <ChevronDown className="w-5 h-5 text-gray-400" />
                              )}
                            </div>
                          </div>

                          {editingPage?.page_key === page.page_key && (
                            <div className="p-4 border-t space-y-6">
                              {/* Hero Section */}
                              <div className="space-y-4">
                                <h4 className="font-medium text-sm text-holiday-purple uppercase tracking-wide">Hero Bölümü</h4>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Hero Görseli URL</label>
                                  <Input
                                    value={editingPage.hero_image || ''}
                                    onChange={(e) => setEditingPage({ ...editingPage, hero_image: e.target.value })}
                                    placeholder="https://example.com/hero-image.jpg"
                                  />
                                  {editingPage.hero_image && (
                                    <img src={editingPage.hero_image} alt="Hero preview" className="mt-2 h-32 object-cover rounded" />
                                  )}
                                </div>

                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Hero Başlığı</label>
                                  <Tabs defaultValue="tr">
                                    <TabsList className="mb-2">
                                      <TabsTrigger value="tr">TR</TabsTrigger>
                                      <TabsTrigger value="en">EN</TabsTrigger>
                                      <TabsTrigger value="de">DE</TabsTrigger>
                                      <TabsTrigger value="ru">RU</TabsTrigger>
                                    </TabsList>
                                    {['tr', 'en', 'de', 'ru'].map((lang) => (
                                      <TabsContent key={lang} value={lang}>
                                        <Input
                                          value={editingPage.hero_title?.[lang] || ''}
                                          onChange={(e) => setEditingPage({
                                            ...editingPage,
                                            hero_title: { ...editingPage.hero_title, [lang]: e.target.value }
                                          })}
                                          placeholder={`Başlık (${lang.toUpperCase()})`}
                                        />
                                      </TabsContent>
                                    ))}
                                  </Tabs>
                                </div>

                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Hero Alt Başlığı</label>
                                  <Tabs defaultValue="tr">
                                    <TabsList className="mb-2">
                                      <TabsTrigger value="tr">TR</TabsTrigger>
                                      <TabsTrigger value="en">EN</TabsTrigger>
                                      <TabsTrigger value="de">DE</TabsTrigger>
                                      <TabsTrigger value="ru">RU</TabsTrigger>
                                    </TabsList>
                                    {['tr', 'en', 'de', 'ru'].map((lang) => (
                                      <TabsContent key={lang} value={lang}>
                                        <Input
                                          value={editingPage.hero_subtitle?.[lang] || ''}
                                          onChange={(e) => setEditingPage({
                                            ...editingPage,
                                            hero_subtitle: { ...editingPage.hero_subtitle, [lang]: e.target.value }
                                          })}
                                          placeholder={`Alt başlık (${lang.toUpperCase()})`}
                                        />
                                      </TabsContent>
                                    ))}
                                  </Tabs>
                                </div>
                              </div>

                              {/* Content Sections */}
                              <div className="space-y-4 pt-4 border-t">
                                <div className="flex items-center justify-between">
                                  <h4 className="font-medium text-sm text-holiday-purple uppercase tracking-wide">İçerik Bölümleri</h4>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleAddSection(page.page_key)}
                                  >
                                    <Plus size={14} className="mr-1" /> Bölüm Ekle
                                  </Button>
                                </div>

                                {editingPage.sections?.length === 0 && (
                                  <p className="text-sm text-gray-500 text-center py-4">Henüz içerik bölümü eklenmemiş</p>
                                )}

                                {editingPage.sections?.map((section, idx) => (
                                  <div key={section.id} className="border rounded-lg p-4 bg-gray-50">
                                    <div className="flex items-center justify-between mb-3">
                                      <span className="text-sm font-medium text-gray-700">Bölüm {idx + 1}</span>
                                      <div className="flex items-center gap-2">
                                        <select
                                          value={section.type}
                                          onChange={(e) => handleUpdateSection(section.id, 'type', e.target.value)}
                                          className="text-sm border rounded px-2 py-1"
                                        >
                                          <option value="text">Metin</option>
                                          <option value="image_text">Görsel + Metin</option>
                                          <option value="features">Özellikler</option>
                                        </select>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          className="text-red-500 h-8 w-8 p-0"
                                          onClick={() => handleRemoveSection(section.id)}
                                        >
                                          <Trash2 size={14} />
                                        </Button>
                                      </div>
                                    </div>

                                    <div className="space-y-3">
                                      <div>
                                        <label className="block text-xs font-medium text-gray-600 mb-1">Bölüm Başlığı</label>
                                        <Tabs defaultValue="tr">
                                          <TabsList className="mb-1 h-7">
                                            <TabsTrigger value="tr" className="text-xs px-2 py-1">TR</TabsTrigger>
                                            <TabsTrigger value="en" className="text-xs px-2 py-1">EN</TabsTrigger>
                                            <TabsTrigger value="de" className="text-xs px-2 py-1">DE</TabsTrigger>
                                            <TabsTrigger value="ru" className="text-xs px-2 py-1">RU</TabsTrigger>
                                          </TabsList>
                                          {['tr', 'en', 'de', 'ru'].map((lang) => (
                                            <TabsContent key={lang} value={lang}>
                                              <Input
                                                value={section.title?.[lang] || ''}
                                                onChange={(e) => handleUpdateSection(section.id, 'title', { ...section.title, [lang]: e.target.value })}
                                                placeholder={`Başlık (${lang.toUpperCase()})`}
                                                className="text-sm"
                                              />
                                            </TabsContent>
                                          ))}
                                        </Tabs>
                                      </div>

                                      <div>
                                        <label className="block text-xs font-medium text-gray-600 mb-1">Bölüm İçeriği</label>
                                        <Tabs defaultValue="tr">
                                          <TabsList className="mb-1 h-7">
                                            <TabsTrigger value="tr" className="text-xs px-2 py-1">TR</TabsTrigger>
                                            <TabsTrigger value="en" className="text-xs px-2 py-1">EN</TabsTrigger>
                                            <TabsTrigger value="de" className="text-xs px-2 py-1">DE</TabsTrigger>
                                            <TabsTrigger value="ru" className="text-xs px-2 py-1">RU</TabsTrigger>
                                          </TabsList>
                                          {['tr', 'en', 'de', 'ru'].map((lang) => (
                                            <TabsContent key={lang} value={lang}>
                                              <textarea
                                                value={section.content?.[lang] || ''}
                                                onChange={(e) => handleUpdateSection(section.id, 'content', { ...section.content, [lang]: e.target.value })}
                                                placeholder={`İçerik (${lang.toUpperCase()})`}
                                                rows={3}
                                                className="w-full text-sm p-2 border rounded-lg"
                                              />
                                            </TabsContent>
                                          ))}
                                        </Tabs>
                                      </div>

                                      {(section.type === 'image_text' || section.type === 'features') && (
                                        <div>
                                          <label className="block text-xs font-medium text-gray-600 mb-1">Görsel URL</label>
                                          <Input
                                            value={section.image || ''}
                                            onChange={(e) => handleUpdateSection(section.id, 'image', e.target.value)}
                                            placeholder="https://example.com/image.jpg"
                                            className="text-sm"
                                          />
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>

                              {/* Save Button */}
                              <div className="flex justify-end gap-2 pt-4 border-t">
                                <Button variant="outline" onClick={() => setEditingPage(null)}>
                                  İptal
                                </Button>
                                <Button
                                  className="bg-holiday-purple"
                                  onClick={() => handlePageUpdate(editingPage.page_key, {
                                    hero_image: editingPage.hero_image,
                                    hero_title: editingPage.hero_title,
                                    hero_subtitle: editingPage.hero_subtitle,
                                    sections: editingPage.sections
                                  })}
                                >
                                  <Save size={16} className="mr-2" /> Sayfayı Kaydet
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Content Tab */}
              {activeTab === 'content' && (
                <div className="space-y-6" data-testid="content-management">
                  {/* Add new content */}
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <h3 className="font-semibold mb-4">Yeni İçerik Ekle</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Anahtar (key)</label>
                        <Input
                          placeholder="hero_title"
                          value={newContent.key}
                          onChange={(e) => setNewContent({ ...newContent, key: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Bölüm (section)</label>
                        <Input
                          placeholder="home"
                          value={newContent.section}
                          onChange={(e) => setNewContent({ ...newContent, section: e.target.value })}
                        />
                      </div>
                    </div>
                    <Tabs defaultValue="tr">
                      <TabsList>
                        <TabsTrigger value="tr">TR</TabsTrigger>
                        <TabsTrigger value="en">EN</TabsTrigger>
                        <TabsTrigger value="de">DE</TabsTrigger>
                        <TabsTrigger value="ru">RU</TabsTrigger>
                      </TabsList>
                      {['tr', 'en', 'de', 'ru'].map((lang) => (
                        <TabsContent key={lang} value={lang}>
                          <textarea
                            className="w-full p-3 border rounded-lg"
                            rows={3}
                            placeholder={`İçerik (${lang.toUpperCase()})`}
                            value={newContent.translations[lang] || ''}
                            onChange={(e) => setNewContent({
                              ...newContent,
                              translations: { ...newContent.translations, [lang]: e.target.value }
                            })}
                          />
                        </TabsContent>
                      ))}
                    </Tabs>
                    <Button onClick={handleAddContent} className="mt-4 bg-holiday-green hover:bg-holiday-green-dark">
                      <Plus size={16} className="mr-2" /> İçerik Ekle
                    </Button>
                  </div>

                  {/* Existing content */}
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <h3 className="font-semibold mb-4">Mevcut İçerikler ({content.length})</h3>
                    <div className="space-y-3">
                      {content.map((item) => (
                        <div key={item.id} className="border rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="text-xs bg-gray-100 px-2 py-1 rounded">{item.section}</span>
                              <span className="font-medium text-sm">{item.key}</span>
                            </div>
                            <div className="flex gap-2">
                              <Button size="sm" variant="outline" onClick={() => setEditingContent(item)}>
                                <Edit2 size={14} />
                              </Button>
                              <Button size="sm" variant="outline" className="text-red-500" onClick={() => handleDeleteContent(item.id)}>
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          </div>
                          {editingContent?.id === item.id ? (
                            <div className="mt-3 space-y-3">
                              <Tabs defaultValue="tr">
                                <TabsList>
                                  <TabsTrigger value="tr">TR</TabsTrigger>
                                  <TabsTrigger value="en">EN</TabsTrigger>
                                  <TabsTrigger value="de">DE</TabsTrigger>
                                  <TabsTrigger value="ru">RU</TabsTrigger>
                                </TabsList>
                                {['tr', 'en', 'de', 'ru'].map((lang) => (
                                  <TabsContent key={lang} value={lang}>
                                    <textarea
                                      value={editingContent.translations[lang] || ''}
                                      onChange={(e) => setEditingContent({
                                        ...editingContent,
                                        translations: { ...editingContent.translations, [lang]: e.target.value }
                                      })}
                                      className="w-full p-3 border rounded-lg"
                                      rows={3}
                                    />
                                  </TabsContent>
                                ))}
                              </Tabs>
                              <div className="flex gap-2">
                                <Button onClick={() => handleContentUpdate(editingContent)} size="sm" className="bg-holiday-purple">
                                  <Save size={14} className="mr-1" /> Kaydet
                                </Button>
                                <Button onClick={() => setEditingContent(null)} size="sm" variant="outline">İptal</Button>
                              </div>
                            </div>
                          ) : (
                            <p className="text-sm text-gray-600 truncate">{item.translations?.tr}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Gallery Tab */}
              {activeTab === 'gallery' && (
                <div className="space-y-6" data-testid="gallery-management">
                  {/* Add new image */}
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <h3 className="font-semibold mb-4">Yeni Görsel Ekle</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <Input
                        placeholder="Görsel URL'si"
                        value={newGalleryImage.url}
                        onChange={(e) => setNewGalleryImage({ ...newGalleryImage, url: e.target.value })}
                      />
                      <select
                        value={newGalleryImage.category}
                        onChange={(e) => setNewGalleryImage({ ...newGalleryImage, category: e.target.value })}
                        className="border rounded-lg px-3 py-2"
                      >
                        {galleryCategories.map((cat) => (
                          <option key={cat.value} value={cat.value}>{cat.label}</option>
                        ))}
                      </select>
                      <Input
                        type="number"
                        placeholder="Sıra"
                        value={newGalleryImage.order}
                        onChange={(e) => setNewGalleryImage({ ...newGalleryImage, order: parseInt(e.target.value) || 0 })}
                      />
                    </div>
                    <Button onClick={handleAddGalleryImage} className="mt-4 bg-holiday-green hover:bg-holiday-green-dark">
                      <Plus size={16} className="mr-2" /> Görsel Ekle
                    </Button>
                  </div>

                  {/* Gallery grid */}
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <h3 className="font-semibold mb-4">Mevcut Görseller ({gallery.length})</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                      {gallery.map((image) => (
                        <div key={image.id} className="relative group">
                          <img
                            src={image.url}
                            alt={image.alt_text?.tr || 'Gallery image'}
                            className="w-full aspect-square object-cover rounded-lg"
                          />
                          <button
                            onClick={() => handleDeleteGalleryImage(image.id)}
                            className="absolute top-2 right-2 bg-red-500 text-white p-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <Trash2 size={14} />
                          </button>
                          <span className="absolute bottom-2 left-2 bg-black/50 text-white text-xs px-2 py-1 rounded">
                            {galleryCategories.find(c => c.value === image.category)?.label || image.category}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Rooms Tab */}
              {activeTab === 'rooms' && (
                <div className="space-y-6" data-testid="rooms-management">
                  {/* Add new room */}
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <h3 className="font-semibold mb-4">Yeni Oda Ekle</h3>
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Görsel URL</label>
                          <Input
                            placeholder="/images/rooms/premium_garden.jpg"
                            value={newRoom.image}
                            onChange={(e) => setNewRoom({ ...newRoom, image: e.target.value })}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Sıra</label>
                          <Input
                            type="number"
                            value={newRoom.order}
                            onChange={(e) => setNewRoom({ ...newRoom, order: parseInt(e.target.value) || 0 })}
                          />
                        </div>
                        <div className="flex items-center pt-6">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={newRoom.highlight}
                              onChange={(e) => setNewRoom({ ...newRoom, highlight: e.target.checked })}
                              className="rounded border-gray-300 text-holiday-purple focus:ring-holiday-purple"
                            />
                            <span className="text-sm font-medium text-gray-700">Öne Çıkar (Highlight)</span>
                          </label>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Diğer Görseller (Virgülle Ayırın)</label>
                        <Input
                          placeholder="/images/rooms/premium_garden.jpg, /images/rooms/premium_sea.jpg"
                          value={newRoom.images.join(', ')}
                          onChange={(e) => setNewRoom({ ...newRoom, images: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                        />
                      </div>

                      <Tabs defaultValue="tr">
                        <TabsList className="mb-2">
                          <TabsTrigger value="tr">TR</TabsTrigger>
                          <TabsTrigger value="en">EN</TabsTrigger>
                          <TabsTrigger value="de">DE</TabsTrigger>
                          <TabsTrigger value="ru">RU</TabsTrigger>
                        </TabsList>
                        {['tr', 'en', 'de', 'ru'].map((lang) => (
                          <TabsContent key={lang} value={lang} className="space-y-3 border p-4 rounded-lg">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <Input
                                placeholder={`Başlık (${lang.toUpperCase()})`}
                                value={newRoom.title[lang] || ''}
                                onChange={(e) => setNewRoom({
                                  ...newRoom,
                                  title: { ...newRoom.title, [lang]: e.target.value }
                                })}
                              />
                              <Input
                                placeholder={`Alt Başlık (${lang.toUpperCase()})`}
                                value={newRoom.subtitle[lang] || ''}
                                onChange={(e) => setNewRoom({
                                  ...newRoom,
                                  subtitle: { ...newRoom.subtitle, [lang]: e.target.value }
                                })}
                              />
                              <Input
                                placeholder={`Boyut / Oda Tipi (${lang.toUpperCase()}) örn: 80 m² (1+1)`}
                                value={newRoom.size[lang] || ''}
                                onChange={(e) => setNewRoom({
                                  ...newRoom,
                                  size: { ...newRoom.size, [lang]: e.target.value }
                                })}
                              />
                            </div>
                            <textarea
                              placeholder={`Açıklama (${lang.toUpperCase()})`}
                              value={newRoom.description[lang] || ''}
                              onChange={(e) => setNewRoom({
                                ...newRoom,
                                description: { ...newRoom.description, [lang]: e.target.value }
                              })}
                              className="w-full p-2 border rounded-lg text-sm"
                              rows={2}
                            />
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <Input
                                placeholder={`Manzaralar (Virgülle ayırın)`}
                                value={(newRoom.views[lang] || []).join(', ')}
                                onChange={(e) => setNewRoom({
                                  ...newRoom,
                                  views: { ...newRoom.views, [lang]: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }
                                })}
                              />
                              <Input
                                placeholder={`Özellikler (Virgülle ayırın)`}
                                value={(newRoom.features[lang] || []).join(', ')}
                                onChange={(e) => setNewRoom({
                                  ...newRoom,
                                  features: { ...newRoom.features, [lang]: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }
                                })}
                              />
                            </div>
                          </TabsContent>
                        ))}
                      </Tabs>

                      <Button onClick={handleAddRoom} className="bg-holiday-green hover:bg-holiday-green-dark">
                        <Plus size={16} className="mr-2" /> Oda Ekle
                      </Button>
                    </div>
                  </div>

                  {/* Existing rooms list */}
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <h3 className="font-semibold mb-4">Mevcut Odalar ({rooms.length})</h3>
                    <div className="space-y-4">
                      {rooms.map((room) => (
                        <div key={room.id} className="border rounded-lg p-4">
                          {editingRoom?.id === room.id ? (
                            <div className="space-y-4">
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Görsel URL</label>
                                  <Input
                                    value={editingRoom.image}
                                    onChange={(e) => setEditingRoom({ ...editingRoom, image: e.target.value })}
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Sıra</label>
                                  <Input
                                    type="number"
                                    value={editingRoom.order}
                                    onChange={(e) => setEditingRoom({ ...editingRoom, order: parseInt(e.target.value) || 0 })}
                                  />
                                </div>
                                <div className="flex items-center pt-6">
                                  <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={editingRoom.highlight}
                                      onChange={(e) => setEditingRoom({ ...editingRoom, highlight: e.target.checked })}
                                      className="rounded border-gray-300 text-holiday-purple focus:ring-holiday-purple"
                                    />
                                    <span className="text-sm font-medium text-gray-700">Öne Çıkar (Highlight)</span>
                                  </label>
                                </div>
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Diğer Görseller (Virgülle Ayırın)</label>
                                <Input
                                  value={(editingRoom.images || []).join(', ')}
                                  onChange={(e) => setEditingRoom({ ...editingRoom, images: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                                />
                              </div>

                              <Tabs defaultValue="tr">
                                <TabsList className="mb-2">
                                  <TabsTrigger value="tr">TR</TabsTrigger>
                                  <TabsTrigger value="en">EN</TabsTrigger>
                                  <TabsTrigger value="de">DE</TabsTrigger>
                                  <TabsTrigger value="ru">RU</TabsTrigger>
                                </TabsList>
                                {['tr', 'en', 'de', 'ru'].map((lang) => (
                                  <TabsContent key={lang} value={lang} className="space-y-3 border p-4 rounded-lg">
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                      <Input
                                        placeholder="Başlık"
                                        value={editingRoom.title?.[lang] || ''}
                                        onChange={(e) => setEditingRoom({
                                          ...editingRoom,
                                          title: { ...editingRoom.title, [lang]: e.target.value }
                                        })}
                                      />
                                      <Input
                                        placeholder="Alt Başlık"
                                        value={editingRoom.subtitle?.[lang] || ''}
                                        onChange={(e) => setEditingRoom({
                                          ...editingRoom,
                                          subtitle: { ...editingRoom.subtitle, [lang]: e.target.value }
                                        })}
                                      />
                                      <Input
                                        placeholder="Boyut / Oda Tipi"
                                        value={editingRoom.size?.[lang] || ''}
                                        onChange={(e) => setEditingRoom({
                                          ...editingRoom,
                                          size: { ...editingRoom.size, [lang]: e.target.value }
                                        })}
                                      />
                                    </div>
                                    <textarea
                                      placeholder="Açıklama"
                                      value={editingRoom.description?.[lang] || ''}
                                      onChange={(e) => setEditingRoom({
                                        ...editingRoom,
                                        description: { ...editingRoom.description, [lang]: e.target.value }
                                      })}
                                      className="w-full p-2 border rounded-lg text-sm"
                                      rows={2}
                                    />
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      <Input
                                        placeholder="Manzaralar (Virgülle ayırın)"
                                        value={(editingRoom.views?.[lang] || []).join(', ')}
                                        onChange={(e) => setEditingRoom({
                                          ...editingRoom,
                                          views: { ...editingRoom.views, [lang]: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }
                                        })}
                                      />
                                      <Input
                                        placeholder="Özellikler (Virgülle ayırın)"
                                        value={(editingRoom.features?.[lang] || []).join(', ')}
                                        onChange={(e) => setEditingRoom({
                                          ...editingRoom,
                                          features: { ...editingRoom.features, [lang]: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }
                                        })}
                                      />
                                    </div>
                                  </TabsContent>
                                ))}
                              </Tabs>

                              <div className="flex gap-2">
                                <Button onClick={() => handleUpdateRoom(editingRoom)} size="sm" className="bg-holiday-purple">
                                  <Save size={14} className="mr-1" /> Kaydet
                                </Button>
                                <Button onClick={() => setEditingRoom(null)} size="sm" variant="outline">İptal</Button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex gap-4">
                              <img src={room.image} alt="" className="w-24 h-16 object-cover rounded" />
                              <div className="flex-1">
                                <h4 className="font-semibold text-gray-900">{room.title?.tr}</h4>
                                <p className="text-sm text-holiday-purple">{room.subtitle?.tr} ({room.size?.tr})</p>
                                <p className="text-xs text-gray-500 mt-1">Sıra: {room.order} • Öne Çıkarılmış: {room.highlight ? 'Evet' : 'Hayır'}</p>
                              </div>
                              <div className="flex gap-2 items-start">
                                <Button size="sm" variant="outline" onClick={() => setEditingRoom(room)}>
                                  <Edit2 size={14} />
                                </Button>
                                <Button size="sm" variant="outline" className="text-red-500" onClick={() => handleDeleteRoom(room.id)}>
                                  <Trash2 size={14} />
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Pools Tab */}
              {activeTab === 'pools' && (
                <div className="space-y-6" data-testid="pools-management">
                  {/* Add new pool */}
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <h3 className="font-semibold mb-4">Yeni Havuz Ekle</h3>
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Boyut (Ölçü)</label>
                          <Input
                            placeholder="780 m²"
                            value={newPool.size}
                            onChange={(e) => setNewPool({ ...newPool, size: e.target.value })}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Derinlik</label>
                          <Input
                            placeholder="150 cm"
                            value={newPool.depth}
                            onChange={(e) => setNewPool({ ...newPool, depth: e.target.value })}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Tip</label>
                          <select
                            value={newPool.type}
                            onChange={(e) => setNewPool({ ...newPool, type: e.target.value })}
                            className="w-full border rounded-lg px-3 py-2 text-sm h-10"
                          >
                            <option value="outdoor">Açık Havuz</option>
                            <option value="kids">Çocuk Havuzu</option>
                            <option value="indoor">Kapalı Havuz</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Sıra</label>
                          <Input
                            type="number"
                            value={newPool.order}
                            onChange={(e) => setNewPool({ ...newPool, order: parseInt(e.target.value) || 0 })}
                          />
                        </div>
                      </div>

                      <Tabs defaultValue="tr">
                        <TabsList className="mb-2">
                          <TabsTrigger value="tr">TR</TabsTrigger>
                          <TabsTrigger value="en">EN</TabsTrigger>
                          <TabsTrigger value="de">DE</TabsTrigger>
                          <TabsTrigger value="ru">RU</TabsTrigger>
                        </TabsList>
                        {['tr', 'en', 'de', 'ru'].map((lang) => (
                          <TabsContent key={lang} value={lang}>
                            <Input
                              placeholder={`Havuz Adı (${lang.toUpperCase()})`}
                              value={newPool.name[lang] || ''}
                              onChange={(e) => setNewPool({
                                ...newPool,
                                name: { ...newPool.name, [lang]: e.target.value }
                              })}
                            />
                          </TabsContent>
                        ))}
                      </Tabs>

                      <Button onClick={handleAddPool} className="bg-holiday-green hover:bg-holiday-green-dark">
                        <Plus size={16} className="mr-2" /> Havuz Ekle
                      </Button>
                    </div>
                  </div>

                  {/* Existing pools list */}
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <h3 className="font-semibold mb-4">Mevcut Havuzlar ({pools.length})</h3>
                    <div className="space-y-4">
                      {pools.map((pool) => (
                        <div key={pool.id} className="border rounded-lg p-4">
                          {editingPool?.id === pool.id ? (
                            <div className="space-y-4">
                              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Boyut (Ölçü)</label>
                                  <Input
                                    value={editingPool.size}
                                    onChange={(e) => setEditingPool({ ...editingPool, size: e.target.value })}
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Derinlik</label>
                                  <Input
                                    value={editingPool.depth}
                                    onChange={(e) => setEditingPool({ ...editingPool, depth: e.target.value })}
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Tip</label>
                                  <select
                                    value={editingPool.type}
                                    onChange={(e) => setEditingPool({ ...editingPool, type: e.target.value })}
                                    className="w-full border rounded-lg px-3 py-2 text-sm h-10"
                                  >
                                    <option value="outdoor">Açık Havuz</option>
                                    <option value="kids">Çocuk Havuzu</option>
                                    <option value="indoor">Kapalı Havuz</option>
                                  </select>
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Sıra</label>
                                  <Input
                                    type="number"
                                    value={editingPool.order}
                                    onChange={(e) => setEditingPool({ ...editingPool, order: parseInt(e.target.value) || 0 })}
                                  />
                                </div>
                              </div>

                              <Tabs defaultValue="tr">
                                <TabsList className="mb-2">
                                  <TabsTrigger value="tr">TR</TabsTrigger>
                                  <TabsTrigger value="en">EN</TabsTrigger>
                                  <TabsTrigger value="de">DE</TabsTrigger>
                                  <TabsTrigger value="ru">RU</TabsTrigger>
                                </TabsList>
                                {['tr', 'en', 'de', 'ru'].map((lang) => (
                                  <TabsContent key={lang} value={lang}>
                                    <Input
                                      placeholder="Havuz Adı"
                                      value={editingPool.name?.[lang] || ''}
                                      onChange={(e) => setEditingPool({
                                        ...editingPool,
                                        name: { ...editingPool.name, [lang]: e.target.value }
                                      })}
                                    />
                                  </TabsContent>
                                ))}
                              </Tabs>

                              <div className="flex gap-2">
                                <Button onClick={() => handleUpdatePool(editingPool)} size="sm" className="bg-holiday-purple">
                                  <Save size={14} className="mr-1" /> Kaydet
                                </Button>
                                <Button onClick={() => setEditingPool(null)} size="sm" variant="outline">İptal</Button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex justify-between items-center">
                              <div>
                                <h4 className="font-semibold text-gray-900">{pool.name?.tr}</h4>
                                <p className="text-sm text-gray-500">{pool.size} • {pool.depth} • Tip: {pool.type === 'outdoor' ? 'Açık' : pool.type === 'indoor' ? 'Kapalı' : 'Çocuk'}</p>
                                <p className="text-xs text-gray-400">Sıra: {pool.order}</p>
                              </div>
                              <div className="flex gap-2">
                                <Button size="sm" variant="outline" onClick={() => setEditingPool(pool)}>
                                  <Edit2 size={14} />
                                </Button>
                                <Button size="sm" variant="outline" className="text-red-500" onClick={() => handleDeletePool(pool.id)}>
                                  <Trash2 size={14} />
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Messages Tab */}
              {activeTab === 'messages' && (
                <div className="bg-white rounded-xl shadow-sm" data-testid="messages-management">
                  <div className="p-4 border-b">
                    <h2 className="font-semibold">İletişim Mesajları ({messages.length})</h2>
                  </div>
                  <div className="divide-y">
                    {messages.length === 0 ? (
                      <div className="p-8 text-center text-gray-500">Henüz mesaj yok</div>
                    ) : (
                      messages.map((message) => (
                        <div key={message.id} className={`p-4 ${!message.is_read ? 'bg-blue-50' : ''}`}>
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-semibold">{message.name}</span>
                                {!message.is_read && (
                                  <span className="bg-blue-500 text-white text-xs px-2 py-0.5 rounded">Yeni</span>
                                )}
                              </div>
                              <p className="text-sm text-gray-500">{message.email} • {message.phone}</p>
                              <p className="text-sm font-medium text-gray-700 mt-2">{message.subject}</p>
                              <p className="text-sm text-gray-600 mt-1">{message.message}</p>
                              <p className="text-xs text-gray-400 mt-2">
                                {new Date(message.created_at).toLocaleString('tr-TR')}
                              </p>
                            </div>
                            <div className="flex gap-2">
                              {!message.is_read && (
                                <Button size="sm" variant="outline" onClick={() => handleMarkMessageRead(message.id)}>
                                  <Eye size={14} />
                                </Button>
                              )}
                              <Button size="sm" variant="outline" className="text-red-500" onClick={() => handleDeleteMessage(message.id)}>
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {/* Settings Tab */}
              {activeTab === 'settings' && (
                <div className="space-y-6" data-testid="settings-management">
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="font-semibold">Site Ayarları</h2>
                      {editingSettings ? (
                        <div className="flex gap-2">
                          <Button onClick={handleSettingsUpdate} className="bg-holiday-purple">
                            <Save size={16} className="mr-2" /> Kaydet
                          </Button>
                          <Button variant="outline" onClick={() => setEditingSettings(false)}>İptal</Button>
                        </div>
                      ) : (
                        <Button onClick={() => setEditingSettings(true)} variant="outline">
                          <Edit2 size={16} className="mr-2" /> Düzenle
                        </Button>
                      )}
                    </div>

                    <Accordion type="multiple" className="space-y-4">
                      {/* Branding */}
                      <AccordionItem value="branding" className="border rounded-lg px-4">
                        <AccordionTrigger className="py-4">Logo & Marka</AccordionTrigger>
                        <AccordionContent className="pb-4 space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Otel Adı</label>
                              <Input
                                value={settings.hotel_name || ''}
                                onChange={(e) => setSettings({ ...settings, hotel_name: e.target.value })}
                                disabled={!editingSettings}
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Logo Metni</label>
                              <Input
                                value={settings.logo_text || ''}
                                onChange={(e) => setSettings({ ...settings, logo_text: e.target.value })}
                                disabled={!editingSettings}
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Logo Alt Metni</label>
                              <Input
                                value={settings.logo_subtitle || ''}
                                onChange={(e) => setSettings({ ...settings, logo_subtitle: e.target.value })}
                                disabled={!editingSettings}
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Logo Görseli URL</label>
                              <Input
                                value={settings.logo_image || ''}
                                onChange={(e) => setSettings({ ...settings, logo_image: e.target.value })}
                                disabled={!editingSettings}
                                placeholder="(opsiyonel)"
                              />
                            </div>
                          </div>
                        </AccordionContent>
                      </AccordionItem>

                      {/* Contact */}
                      <AccordionItem value="contact" className="border rounded-lg px-4">
                        <AccordionTrigger className="py-4">İletişim Bilgileri</AccordionTrigger>
                        <AccordionContent className="pb-4 space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Telefon</label>
                              <Input
                                value={settings.phone || ''}
                                onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
                                disabled={!editingSettings}
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">E-posta</label>
                              <Input
                                value={settings.email || ''}
                                onChange={(e) => setSettings({ ...settings, email: e.target.value })}
                                disabled={!editingSettings}
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Rezervasyon E-posta</label>
                              <Input
                                value={settings.reservation_email || ''}
                                onChange={(e) => setSettings({ ...settings, reservation_email: e.target.value })}
                                disabled={!editingSettings}
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp</label>
                              <Input
                                value={settings.whatsapp || ''}
                                onChange={(e) => setSettings({ ...settings, whatsapp: e.target.value })}
                                disabled={!editingSettings}
                              />
                            </div>
                          </div>
                        </AccordionContent>
                      </AccordionItem>

                      {/* Social Media */}
                      <AccordionItem value="social" className="border rounded-lg px-4">
                        <AccordionTrigger className="py-4">Sosyal Medya</AccordionTrigger>
                        <AccordionContent className="pb-4 space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Facebook</label>
                              <Input
                                value={settings.social_media?.facebook || ''}
                                onChange={(e) => setSettings({ ...settings, social_media: { ...settings.social_media, facebook: e.target.value } })}
                                disabled={!editingSettings}
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Instagram</label>
                              <Input
                                value={settings.social_media?.instagram || ''}
                                onChange={(e) => setSettings({ ...settings, social_media: { ...settings.social_media, instagram: e.target.value } })}
                                disabled={!editingSettings}
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">LinkedIn</label>
                              <Input
                                value={settings.social_media?.linkedin || ''}
                                onChange={(e) => setSettings({ ...settings, social_media: { ...settings.social_media, linkedin: e.target.value } })}
                                disabled={!editingSettings}
                                placeholder="https://www.linkedin.com/company/holiday-inn-resort-bodrum-west-beach/"
                              />
                            </div>
                          </div>
                        </AccordionContent>
                      </AccordionItem>

                      {/* Reservation */}
                      <AccordionItem value="reservation" className="border rounded-lg px-4">
                        <AccordionTrigger className="py-4">Rezervasyon Ayarları</AccordionTrigger>
                        <AccordionContent className="pb-4 space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Rezervasyon URL</label>
                            <Input
                              value={settings.reservation_url || ''}
                              onChange={(e) => setSettings({ ...settings, reservation_url: e.target.value })}
                              disabled={!editingSettings}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Rezervasyon Widget Kodu</label>
                            <textarea
                              value={settings.reservation_code || ''}
                              onChange={(e) => setSettings({ ...settings, reservation_code: e.target.value })}
                              disabled={!editingSettings}
                              className="w-full p-3 border rounded-lg text-sm"
                              rows={4}
                              placeholder="Holiday Inn rezervasyon widget kodunu buraya yapıştırın"
                            />
                          </div>
                        </AccordionContent>
                      </AccordionItem>

                      {/* Map */}
                      <AccordionItem value="map" className="border rounded-lg px-4">
                        <AccordionTrigger className="py-4">Harita Ayarları</AccordionTrigger>
                        <AccordionContent className="pb-4 space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Enlem (Lat)</label>
                              <Input
                                value={settings.google_maps_lat || ''}
                                onChange={(e) => setSettings({ ...settings, google_maps_lat: e.target.value })}
                                disabled={!editingSettings}
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Boylam (Lng)</label>
                              <Input
                                value={settings.google_maps_lng || ''}
                                onChange={(e) => setSettings({ ...settings, google_maps_lng: e.target.value })}
                                disabled={!editingSettings}
                              />
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Google Maps Embed Kodu</label>
                            <textarea
                              value={settings.google_maps_embed || ''}
                              onChange={(e) => setSettings({ ...settings, google_maps_embed: e.target.value })}
                              disabled={!editingSettings}
                              className="w-full p-3 border rounded-lg text-sm"
                              rows={3}
                            />
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  </div>
                </div>
              )}

              {/* Security Tab */}
              {activeTab === 'security' && (
                <div className="max-w-md" data-testid="security-management">
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <h2 className="font-semibold mb-6">Şifre Değiştir</h2>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Mevcut Şifre</label>
                        <Input
                          type="password"
                          value={passwordData.old_password}
                          onChange={(e) => setPasswordData({ ...passwordData, old_password: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Yeni Şifre</label>
                        <Input
                          type="password"
                          value={passwordData.new_password}
                          onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Yeni Şifre (Tekrar)</label>
                        <Input
                          type="password"
                          value={passwordData.confirm_password}
                          onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                        />
                      </div>
                      <Button onClick={handlePasswordChange} className="w-full bg-holiday-purple">
                        Şifreyi Değiştir
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
